import React, { useState } from "react";
import {
  Layout,
  Menu,
  Avatar,
  Drawer,
  Badge,
  Switch,
  Grid,
  Card,
  Row,
  Col,
  Typography,
  Divider,
} from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  TeamOutlined,
  SettingOutlined,
  AppstoreOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

import logo from "./Logo/zulu.png";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

const SalesDashboard = () => {
  const screens = useBreakpoint();

  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const toggleSidebar = () => setCollapsed(!collapsed);
  const toggleDrawer = () => setDrawerVisible(!drawerVisible);
  const toggleTheme = () => setDarkMode(!darkMode);

  const isDark = darkMode;

  const menuItems = [
    { key: "1", icon: <DashboardOutlined />, label: "Overview" },
    { key: "2", icon: <ShoppingCartOutlined />, label: "Orders" },
    { key: "3", icon: <AppstoreOutlined />, label: "Products" },
    { key: "4", icon: <TeamOutlined />, label: "Customers" },
    { key: "5", icon: <BarChartOutlined />, label: "Analytics" },
    { key: "6", icon: <SettingOutlined />, label: "Settings" },
  ];

  const sidebar = (
    <>
      {!collapsed && (
        <div style={{ padding: 20, textAlign: "center" }}>
          <Avatar
            size={64}
            icon={<UserOutlined />}
            style={{
              backgroundColor: isDark ? "#374151" : "#2563eb",
            }}
          />

          <div style={{ marginTop: 10, lineHeight: 1.2 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: isDark ? "#ffffff" : "#111827",
              }}
            >
              Sales Agent
            </div>

            <div
              style={{
                fontSize: 13,
                marginTop: 3,
                color: isDark ? "#9ca3af" : "#6b7280",
              }}
            >
              Store Management System
            </div>
          </div>
        </div>
      )}

      <Menu
        theme={isDark ? "dark" : "light"}
        mode="inline"
        defaultSelectedKeys={["1"]}
        items={menuItems}
      />
    </>
  );

  return (
    <Layout
      style={{
        minHeight: "100vh",
        background: isDark ? "#0b0f19" : "#f5f7fb",
      }}
    >
      {/* SIDEBAR */}
      {screens.md && (
        <Sider
          collapsible
          collapsed={collapsed}
          trigger={null}
          width={260}
          theme={isDark ? "dark" : "light"}
        >
          {sidebar}
        </Sider>
      )}

      {/* MOBILE DRAWER */}
      {!screens.md && (
        <Drawer
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          bodyStyle={{ padding: 0 }}
        >
          {sidebar}
        </Drawer>
      )}

      <Layout>
        {/* HEADER */}
        <Header
          style={{
            background: isDark ? "#111827" : "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 20px",
            borderBottom: isDark
              ? "1px solid #1f2937"
              : "1px solid #e5e7eb",
            boxShadow: isDark
              ? "none"
              : "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          {/* LEFT */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {screens.md ? (
              collapsed ? (
                <MenuUnfoldOutlined
                  onClick={toggleSidebar}
                  style={{
                    fontSize: 20,
                    color: isDark ? "#ffffff" : "#111827",
                  }}
                />
              ) : (
                <MenuFoldOutlined
                  onClick={toggleSidebar}
                  style={{
                    fontSize: 20,
                    color: isDark ? "#ffffff" : "#111827",
                  }}
                />
              )
            ) : (
              <MenuUnfoldOutlined onClick={toggleDrawer} />
            )}

            <img src={logo} alt="logo" style={{ width: 38 }} />

            {/* HEADER TEXT STACK FIX */}
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: isDark ? "#ffffff" : "#111827",
                }}
              >
                Store Sales Dashboard
              </div>

              <div
                style={{
                  fontSize: 13,
                  marginTop: 3,
                  color: isDark ? "#9ca3af" : "#6b7280",
                }}
              >
                Inventory & Sales Control
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <Switch
              checkedChildren="🌙"
              unCheckedChildren="☀️"
              onChange={toggleTheme}
              style={{
                background: isDark ? "#374151" : "#e5e7eb",
              }}
            />

            <Badge count={5}>
              <BellOutlined
                style={{
                  fontSize: 20,
                  color: isDark ? "#ffffff" : "#111827",
                }}
              />
            </Badge>

            <Avatar
              icon={<UserOutlined />}
              style={{
                backgroundColor: isDark ? "#374151" : "#2563eb",
              }}
            />
          </div>
        </Header>

        {/* CONTENT */}
        <Content style={{ padding: 24 }}>
          <Title
            level={3}
            style={{
              color: isDark ? "#f9fafb" : "#111827",
              marginBottom: 4,
              fontWeight: 700,
            }}
          >
            Welcome back, Sales Agent 👋
          </Title>

          <Text
            style={{
              color: isDark ? "#d1d5db" : "#4b5563",
              fontSize: 14,
            }}
          >
            Here is your store performance overview
          </Text>

          <Divider />

          {/* CARDS */}
          <Row gutter={[16, 16]}>
            {[
              {
                icon: <DollarOutlined />,
                value: "$12,450",
                label: "Total Sales",
                color: "#52c41a",
              },
              {
                icon: <ShoppingCartOutlined />,
                value: "320",
                label: "Orders",
                color: "#1890ff",
              },
              {
                icon: <AppstoreOutlined />,
                value: "85",
                label: "Products",
                color: "#faad14",
              },
              {
                icon: <TeamOutlined />,
                value: "1,240",
                label: "Customers",
                color: "#722ed1",
              },
            ].map((item, i) => (
              <Col xs={24} sm={12} lg={6} key={i}>
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 12,
                    boxShadow: isDark
                      ? "none"
                      : "0 4px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <div style={{ fontSize: 28, color: item.color }}>
                    {item.icon}
                  </div>

                  <Title level={3} style={{ marginTop: 10 }}>
                    {item.value}
                  </Title>

                  <Text
                    style={{
                      color: isDark ? "#d1d5db" : "#6b7280",
                      fontSize: 14,
                    }}
                  >
                    {item.label}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default SalesDashboard;

